import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {useUserLoggedIn} from './userContext';
import {WeatherService} from '../api';
import {WeatherStationFromList} from '../api/types';
import {
  addFavoriteStation,
  removeFavoriteStation,
  selectFavoriteStationIds,
  setAlert,
} from '../store';
import {CommonRootState} from '../store/types';
import useApiClient from './useApiClient';
import {trackCoreEvent} from '../utils/Analytics/sink';

const useFavoriteStation = () => {
  const {fetch} = useApiClient();
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();

  const isLoggedIn = useUserLoggedIn();
  const favoriteIds = useSelector(selectFavoriteStationIds);
  // Read when the server answers, not at the tap: signed in, the star only
  // flips on that answer, so a second tap sends a second request whose
  // answer must change nothing (the reducer does not dedupe).
  const store = useStore<CommonRootState>();
  const isFavorite = React.useCallback(
    (stationId: number) =>
      selectFavoriteStationIds(store.getState()).includes(stationId),
    [store],
  );

  const addFavorite = React.useCallback(
    (stationId: number) => {
      if (isLoggedIn) {
        fetch(WeatherService.addFavoriteStation(stationId))
          .then((station: WeatherStationFromList) => {
            if (isFavorite(station.id)) {
              return;
            }
            dispatch(addFavoriteStation(station.id));
            trackCoreEvent({name: 'favorite_add', params: {kind: 'station'}});
            dispatch(
              setAlert({
                type: 'INFO',
                message: formatMessage(
                  messages.stations.stationAddedToFavorites,
                ),
              }),
            );
          })
          .catch(() => {});
      } else {
        dispatch(addFavoriteStation(stationId));
        trackCoreEvent({name: 'favorite_add', params: {kind: 'station'}});
        dispatch(
          setAlert({
            type: 'INFO',
            message: formatMessage(messages.stations.stationAddedToFavorites),
          }),
        );
      }
    },
    [dispatch, fetch, formatMessage, isFavorite, isLoggedIn],
  );

  const removeFavorite = React.useCallback(
    (stationId: number) => {
      if (isLoggedIn) {
        fetch(WeatherService.removeFavoriteStation(stationId))
          .then(() => {
            if (!isFavorite(stationId)) {
              return;
            }
            dispatch(removeFavoriteStation(stationId));
            trackCoreEvent({
              name: 'favorite_remove',
              params: {kind: 'station'},
            });
            dispatch(
              setAlert({
                type: 'INFO',
                message: formatMessage(
                  messages.stations.stationRemovedFromFavorites,
                ),
              }),
            );
          })
          .catch(() => {});
      } else {
        dispatch(removeFavoriteStation(stationId));
        trackCoreEvent({name: 'favorite_remove', params: {kind: 'station'}});
        dispatch(
          setAlert({
            type: 'INFO',
            message: formatMessage(
              messages.stations.stationRemovedFromFavorites,
            ),
          }),
        );
      }
    },
    [dispatch, fetch, formatMessage, isFavorite, isLoggedIn],
  );

  return {addFavorite, removeFavorite, favoriteIds};
};

export default useFavoriteStation;
